import React, { Component } from 'react';
import PropTypes from 'prop-types';

import Typography from '@mui/material/Typography';
import FormControlLabel from '@mui/material/FormControlLabel';
import Collapse from '@mui/material/Collapse';
import Checkbox from '@mui/material/Checkbox';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import { styled } from '@mui/material/styles';

import ConditionalSentiment from './ConditionalSentiment'
import CONSTANTS from '../utils/Constants';

const FormContainer = styled('div')(({ theme }) => ({
	marginTop: theme.spacing(2),
	paddingLeft: theme.spacing(3.5),
	paddingRight: theme.spacing(3.5)
}));

const SecondaryFields = styled('div')(({ theme }) => ({
	marginTop: theme.spacing(2),
}));

const HiddenField = styled(TextField)({
	position: "absolute !important",
	height: "1px",
	width: "1px",
	overflow: "hidden",
	clip: "rect(1px, 1px, 1px, 1px)"
});

const AnimatedField = styled(TextField)(({ visible }) => ({
	transition: visible 
		? 'width 0.75s cubic-bezier(0.000, 0.795, 0.000, 1.000), opacity 0.3s ease-out'
		: 'width 0.75s ease-in, opacity 0.74s cubic-bezier(0.95, 0.05, 0.795, 0.035)',
	width: visible ? "100%" : 0,
	opacity: visible ? 1 : 0
}));

const AnimatedTypography = styled(Typography)(({ visible }) => ({
	transition: visible 
		? 'width 0.75s cubic-bezier(0.000, 0.795, 0.000, 1.000), opacity 0.3s ease-out'
		: 'width 0.75s ease-in, opacity 0.74s cubic-bezier(0.95, 0.05, 0.795, 0.035)',
	width: visible ? "100%" : 0,
	opacity: visible ? 1 : 0
}));

// Email validation function
const isValidEmail = (email) => {
	const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
	return emailRegex.test(email);
};

class SubmissionForm extends Component {
	render() {
		let {
			identify, 
			identifyName, 
			identifyEmail, 
			identifyPhone, 
			identifyHandler, 
			showConcernOther, 
			concernOther, 
			fieldChangeHandler, 
			concernOtherHandler, 
			concernOtherDescription, 
			sentimentProps, 
			description, 
			identifyEmailConf, 
			descriptionLabel, 
			placeholder
		} = this.props;

		const emailError = identifyEmail && !isValidEmail(identifyEmail);

		return (
			<FormContainer>
				<Grid container direction="column" alignItems="stretch" spacing={2}>
					<Grid item>
						<ConditionalSentiment {...sentimentProps} />
					</Grid>
					<Grid item>
						<Typography variant="subtitle1">{descriptionLabel}</Typography>
						<TextField 
							multiline 
							fullWidth
							id="description"
							name="description"
							aria-label={descriptionLabel}
							value={description}
							rows={4}
							placeholder={placeholder}
							onChange={fieldChangeHandler}
							margin="none"
							variant="outlined"
						/>
					</Grid>
					<Grid item>
						<FormControlLabel
							control={
								<Checkbox
									value="identify"
									aria-label="Identify yourself?"
									checked={identify}
									color="primary"
									onChange={identifyHandler}
								/>
							}
							label="Would you like to identify yourself?"
						/>
						<Collapse in={identify} timeout={850}>
							<SecondaryFields>
								<Grid container direction="column" alignItems="stretch" spacing={2}>
									<Grid item>
										<AnimatedField 
											fullWidth
											id="identifyName"
											name="identifyName"
											aria-label="Name"
											label="Name"
											placeholder="First & Last Name"
											margin="none"
											variant="outlined"
											value={identifyName}
											onChange={fieldChangeHandler}
											visible={identify}
										/>
									</Grid>
									<Grid item>
										<AnimatedField 
											fullWidth
											id="identifyPhone"
											name="identifyPhone"
											label="Phone"
											aria-label="Phone"
											placeholder=""
											margin="none"
											variant="outlined"
											value={identifyPhone}
											onChange={fieldChangeHandler}
											visible={identify}
										/>
									</Grid>
									<Grid item>
										<AnimatedField 
											fullWidth
											label="Email"
											name="identifyEmail"
											aria-label="Email"
											placeholder=""
											margin="none"
											variant="outlined"
											value={identifyEmail}
											onChange={fieldChangeHandler}
											visible={identify}
											error={emailError}
											helperText={emailError ? 'email is not valid' : ''}
										/>
										<HiddenField 
											fullWidth
											id="identifyEmailConf"
											name="identifyEmailConf"
											label="Confirm Address"
											aria-label="Confirm Address"
											aria-hidden="true"
											placeholder=""
											inputProps={{
												tabIndex: "-1",
												autoComplete: "false"
											}}
											margin="none"
											variant="outlined"
											value={identifyEmailConf}
											onChange={fieldChangeHandler}
										/>
									</Grid>
								</Grid>
							</SecondaryFields>
						</Collapse>
					</Grid>
					{showConcernOther ?
					<Grid item>
						<FormControlLabel
							control={
								<Checkbox
									value="concernOther"
									aria-label="Concerned for someone else?"
									checked={concernOther}
									color="primary"
									onChange={concernOtherHandler}
								/>
							}
							label="Are you concerned for someone other than yourself?"
						/>
						<Collapse in={concernOther} timeout={850}>
							<SecondaryFields>
								<Grid container direction="column" alignItems="stretch" spacing={2}>
									<Grid item>
										<AnimatedTypography 
											variant="subtitle1"
											visible={concernOther}
										>
											Who? Why?
										</AnimatedTypography>
										<AnimatedField 
											fullWidth 
											multiline
											id="concernOtherDescription"
											name="concernOtherDescription"
											aria-label="Name or description"
											rows={4}
											placeholder="Please describe as thoroughly as you can, names, nicknames, friends, which school they attend, etc. Anything helpful."
											margin="none"
											variant="outlined"
											onChange={fieldChangeHandler}
											value={concernOtherDescription}
											visible={concernOther}
										/>
									</Grid>
								</Grid>
							</SecondaryFields>
						</Collapse>
					</Grid>
					: null}
				</Grid>
			</FormContainer>
		);
	}
}

SubmissionForm.propTypes = {
	showConcernOther: PropTypes.bool,
	sentimentProps: PropTypes.object.isRequired,

	identifyHandler: PropTypes.func.isRequired,
	fieldChangeHandler: PropTypes.func.isRequired,

	placeholder: PropTypes.string.isRequired,
	descriptionLabel: PropTypes.string.isRequired,

	identify: PropTypes.bool,
	identifyName: PropTypes.string.isRequired,
	identifyEmail: PropTypes.string.isRequired,
	identifyPhone: PropTypes.string.isRequired,
	identifyEmailConf: PropTypes.string.isRequired,
	concernOther: PropTypes.bool,
	description: PropTypes.string.isRequired,
	concernOtherHandler: PropTypes.func.isRequired,
	concernOtherDescription: PropTypes.string.isRequired
};

export default SubmissionForm;